import { Box, Button, Chip, LinearProgress, Typography, useTheme } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { ScoringBatch } from '../types';

interface ScoringBatchStatusCardProps {
    batch: ScoringBatch;
    onRetry?: () => void;
    retryLoading?: boolean;
}

const CountChip = ({ label, value, color }: { label: string; value: number; color: string }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color }}>
            {value}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {label}
        </Typography>
    </Box>
);

export const ScoringBatchStatusCard = ({
    batch,
    onRetry,
    retryLoading,
}: ScoringBatchStatusCardProps) => {
    const theme = useTheme();
    const isComplete = batch.pending === 0 && batch.inProgress === 0;
    const progress = batch.total > 0 ? ((batch.done + batch.error) / batch.total) * 100 : 0;

    return (
        <Box
            sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${theme.palette.border.main}`,
                backgroundColor: theme.palette.background.paper,
                mb: 3,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Progression du batch
                    </Typography>
                    <Chip
                        label={isComplete ? 'Terminé' : 'En cours'}
                        size="small"
                        sx={{
                            backgroundColor: isComplete
                                ? `${theme.palette.successGreen.main}22`
                                : `${theme.palette.warningOrange.main}22`,
                            color: isComplete
                                ? theme.palette.successGreen.main
                                : theme.palette.warningOrange.main,
                            fontWeight: 600,
                        }}
                    />
                </Box>
                {batch.error > 0 && onRetry && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ReplayIcon />}
                        onClick={onRetry}
                        disabled={retryLoading}
                        sx={{ borderColor: theme.palette.errorRed.main, color: theme.palette.errorRed.main }}
                    >
                        Relancer les erreurs
                    </Button>
                )}
            </Box>

            <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                    mb: 3,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: theme.palette.action.hover,
                    '& .MuiLinearProgress-bar': {
                        backgroundColor:
                            batch.error > 0 && isComplete
                                ? theme.palette.warningOrange.main
                                : theme.palette.successGreen.main,
                    },
                }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                <CountChip
                    label="Total"
                    value={batch.total}
                    color={theme.palette.text.primary}
                />
                <CountChip
                    label="Terminés"
                    value={batch.done}
                    color={theme.palette.successGreen.main}
                />
                <CountChip
                    label="En attente"
                    value={batch.pending + batch.inProgress}
                    color={theme.palette.warningOrange.main}
                />
                <CountChip
                    label="Erreurs"
                    value={batch.error}
                    color={theme.palette.errorRed.main}
                />
            </Box>
        </Box>
    );
};

export default ScoringBatchStatusCard;
